// src/pages/Parametros/ParametrosPage.jsx
import React, { useRef, useState } from 'react';
import {
    Box, Typography, Tabs, Tab, Paper, Button,
    CircularProgress, Tooltip, useTheme,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import { ParametrosAnaliseProvider, useParametrosAnalise } from '../../context/ParametrosAnaliseContext';
import PageHeader from '../../components/PageHeader';
import { SnackbarAlert } from '../../components/SnackbarAlert';
import TabTempo from './tabs/TabTempo';
import TabTemperatura from './tabs/TabTemperatura';
import TabCrescimento from './tabs/TabCrescimento';
import TabPorMetrica from './tabs/TabPorMetrica';
import TabDuplicata from './tabs/TabDuplicata';
import TabRelatorio from './tabs/TabRelatorio';
import TabDump from './tabs/TabDump';
import TabCor from './tabs/TabCor';
import TabValidation from './tabs/TabValidation';
import TabValidosIA from './tabs/TabValidosIA';

const TABS = [
    { label: 'Tempo', Component: TabTempo },
    { label: 'Temperatura', Component: TabTemperatura },
    { label: 'Crescimento', Component: TabCrescimento },
    { label: 'Por Métrica', Component: TabPorMetrica },
    { label: 'Duplicata', Component: TabDuplicata },
    { label: 'Relatório', Component: TabRelatorio },
    { label: 'Cor', Component: TabCor },
    { label: 'Dump', Component: TabDump },
    { label: 'Validation', Component: TabValidation },
    { label: 'Válidos IA', Component: TabValidosIA },
];

function ParametrosContent() {
    const { params, loading, isDirty, save, reset, doExport, doImport } =
        useParametrosAnalise();
    const [tab, setTab] = useState(0);
    const [saving, setSaving] = useState(false);
    const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });
    const importRef = useRef(null);
    const theme = useTheme();

    const showSnack = (severity, message) =>
        setSnack({ open: true, severity, message });

    const handleSave = async () => {
        setSaving(true);
        try {
            await save();
            showSnack('success', 'Parâmetros salvos com sucesso');
        } catch (e) {
            showSnack('error', e?.message || 'Erro ao salvar parâmetros');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        try {
            await reset();
            showSnack('info', 'Parâmetros restaurados para o padrão');
        } catch {
            showSnack('error', 'Erro ao restaurar padrões');
        }
    };

    const handleImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            await doImport(file);
            showSnack('success', 'Configuração importada — clique em Salvar para confirmar');
        } catch (e) {
            showSnack('error', e?.message || 'Arquivo inválido');
        } finally {
            e.target.value = '';
        }
    };

    if (loading && !params) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 300 }}>
                <CircularProgress />
            </Box>
        );
    }

    const { Component: ActiveTab } = TABS[tab];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px)', pb: '80px' }}>
            {/* Header */}
            <Box sx={{ px: { xs: 2, md: 4 }, pt: 3, pb: 1 }}>
                <PageHeader
                    title="Parâmetros de Análise"
                    subtitle="Configurações dinâmicas por tenant. Alterações afetam todas as análises futuras."
                />
            </Box>

            {/* Tabs */}
            <Box sx={{ px: { xs: 2, md: 4 }, borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {TABS.map(({ label }, i) => (
                        <Tab key={i} label={label} />
                    ))}
                </Tabs>
            </Box>

            {/* Tab content */}
            <Box sx={{ flex: 1, px: { xs: 2, md: 4 }, py: 2 }}>
                {params && <ActiveTab />}
            </Box>

            {/* Fixed footer */}
            <Paper
                elevation={4}
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    px: { xs: 2, md: 4 },
                    py: 1.5,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    borderTop: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Button
                    startIcon={<Lucide.RotateCcw />}
                    onClick={handleReset}
                    color="inherit"
                    size="small"
                >
                    Restaurar padrão
                </Button>
                <Button
                    startIcon={<Lucide.Download />}
                    onClick={doExport}
                    color="inherit"
                    size="small"
                >
                    Exportar
                </Button>
                <Button
                    startIcon={<Lucide.Upload />}
                    onClick={() => importRef.current?.click()}
                    color="inherit"
                    size="small"
                >
                    Importar
                </Button>
                <input
                    ref={importRef}
                    type="file"
                    accept=".json"
                    hidden
                    onChange={handleImport}
                />
                <Tooltip title={!isDirty ? 'Sem alterações pendentes' : ''}>
                    <span>
                        <Button
                            variant="contained"
                            startIcon={
                                saving
                                    ? <CircularProgress size={16} color="inherit" />
                                    : <Lucide.Save />
                            }
                            onClick={handleSave}
                            disabled={saving || !isDirty}
                        >
                            Salvar
                        </Button>
                    </span>
                </Tooltip>
            </Paper>

            <SnackbarAlert
                open={snack.open}
                onClose={() => setSnack((p) => ({ ...p, open: false }))}
                severity={snack.severity}
                message={snack.message}
            />
        </Box>
    );
}

export default function ParametrosPage() {
    return (
        <ParametrosAnaliseProvider>
            <ParametrosContent />
        </ParametrosAnaliseProvider>
    );
}
